import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Stepper from './Stepper';
import InformationForm from './InformationForm';
import InsuranceForm from './InsuranceForm';
import AccidentForm from './AccidentForm';
import CommonForm from './CommonForm';
import { useMutation } from '@tanstack/react-query';
import { createAppointment, CreateAppointmentPayload } from '@/api/appointment';
import Description from '@/components/Description';
import { useNavigate } from 'react-router-dom';
import { kakaoLogin } from '@/api/auth';
import * as Sentry from '@sentry/react';
import { getWithExpiry } from '@/utils/storage';

const AppointmentPage = () => {
  const navigate = useNavigate();
  const raw = getWithExpiry('affiliated');
  const affiliated = raw?.trim().toLowerCase();

  const [form, setForm] = useState({
    name: '',
    phoneNumber: '',
    insuranceType: '손해보험',
    insuranceName: '',
    // address: '',
    hospitalAddress: '',
    accidentType: '',
    insuranceAmount: '',
    description: '',
  });
  const [disabled, setDisabled] = useState(true);

  // ✅ 로컬스토리지에서 저장된 form 복원
  useEffect(() => {
    const savedForm = localStorage.getItem('appointmentForm');
    if (savedForm) {
      setForm(JSON.parse(savedForm));
      localStorage.removeItem('appointmentForm');
    }
  }, []);

  useEffect(() => {
    const {
      name,
      phoneNumber,
      insuranceName,
      // address,
      hospitalAddress,
      accidentType,
      insuranceAmount,
      description,
    } = form;
    setDisabled(
      !(
        name &&
        phoneNumber &&
        insuranceName &&
        // address &&
        hospitalAddress &&
        accidentType &&
        insuranceAmount &&
        description
      ),
    );
  }, [form]);

  const sendMessage = async (phoneNumber: string) => {
    const apiUrl = 'https://alimtalk-api.bizmsg.kr/v2/sender/send';
    const data = [
      {
        message_type: 'AT',
        tmplId: 'allbadeum001',
        phn: phoneNumber.replace(/-/g, ''),
        profile: import.meta.env.VITE_ALIMTALK_PROFILE,
        // profile: '3436a85c6a7923ddc882d21e8484a1c44b367da0',
        msg: [
          '[올받음안내]',
          '안녕하세요, 올받음 입니다.',
          '문의가 잘 접수되었습니다.',
          '평일 오후 4시 이전 접수해주신 문의는 당일 답변 드립니다.',
          '만약 주말, 공휴일인 경우는 무료선임 신청 기한에 포함되지 않으니 염려하지 않으셔도 되고, 다음 영업일에 순차적으로 연락 드립니다.',
          '기타 접수 내용 변경/취소 및 문의사항은 아래 카카오톡으로 문의 주세요.',
          'http://pf.kakao.com/_jSxgGxj/chat',
        ].join('\n'),

        smsSender: '01051482855',
      },
    ];

    const headers = {
      'Content-Type': 'application/json',
      userId: import.meta.env.VITE_ALIMTALK_USER_ID,
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error response:', errorData); // 상세한 에러 메시지 출력
        throw new Error(`Network response was not ok: ${errorData.message}`);
      }

      const responseData = await response.json();
      console.log('Message sent successfully:', responseData);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const getUserIdFromLocalStorage = (): number | null => {
    try {
      const raw = localStorage.getItem('auth-storage');
      const parsed = JSON.parse(raw ?? '{}');
      return parsed?.state?.user?.id ?? null;
    } catch (e) {
      console.error('[❌ 로컬스토리지 파싱 실패]', e);
      return null;
    }
  };

  // const isValidDate = (dateStr: string) => {
  //   // 정규식으로 YYYY-MM-DD 형식 확인
  //   const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  //   if (!dateRegex.test(dateStr)) return false;

  //   // 실제 날짜 객체로 변환하여 유효성 확인
  //   const date = new Date(dateStr);
  //   return !isNaN(date.getTime());
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;

    // if (!isValidDate(form.claimedAt)) {
    //   alert('청구일자를 YYYY-MM-DD 형식으로 정확히 입력해주세요.');
    //   return;
    // }

    const userId = getUserIdFromLocalStorage();
    if (!userId) {
      localStorage.setItem('postLoginRedirect', '/appointment'); // ✅ 리다이렉트 경로 저장
      localStorage.setItem('appointmentForm', JSON.stringify(form)); // ✅ form 저장

      try {
        const { url } = await kakaoLogin();
        window.location.href = url;
      } catch (error) {
        console.error('❌ 카카오 로그인 요청 실패:', error);
        alert('카카오 로그인에 실패했습니다.');
      }

      return;
    }

    // const claimedAt =
    //   form.claimedAt.trim() !== ''
    //     ? `${form.claimedAt}T00:00:00`
    //     : new Date().toISOString().slice(0, 19); // fallback
    const payload: CreateAppointmentPayload = {
      userId,
      adjusterId: userId,
      clientId: userId,
      receptionChannel: affiliated === 'gnnet' ? 'gnnet' : affiliated === 'yt' ? 'yt' : '올받음',
      insuranceCompanyName: form.insuranceName,
      customerName: form.name,
      customerContacts: [form.phoneNumber],
      consultType: 'FREE_APPOINT',
      consultProgressStatus: 'REQUESTED',
      insuranceProduct: '기타',
      treatmentContent: form.accidentType,
      detailedTreatmentContent: form.description,
      isInsurancePayment: true,
      finalLossAdjuster: '',
      totalFee: form.insuranceAmount,
      defaultPayment: 0,
      extraPayment: 0,
      hospitals: [
        {
          hospitalId: 0,
          hospitalName: form.hospitalAddress,
        },
      ],
      // region: form.address,
      isWhatsonExposed: true,
      requestedAt: new Date().toISOString(),
      isReviewExposed: true,
      claimProcess: '',
      insuredName: form.name,
    };

    mutation.mutate(payload);
  };

  const mutation = useMutation({
    mutationFn: createAppointment,
    onSuccess: async () => {
      alert('접수가 완료되었습니다.');
      await sendMessage(form.phoneNumber); // ✅ 전송 완료 후 페이지 이동
      navigate('/appointment/success');
    },
    onError: (err: any) => {
      console.error('접수 실패:', err);

      if (err?.response?.status === 401 || err?.message?.includes('401')) {
        alert('로그인이 필요합니다.');
        return;
      }

      Sentry.captureException(err, {
        tags: { page: 'AppointmentPage' },
        extra: { form },
      });
      alert('접수 중 오류가 발생했습니다.');
    },
  });

  return (
    <>
      <Description />
      <TitleWrapper>
        <TitleInner>무료상담 신청</TitleInner>
      </TitleWrapper>
      <Container>
        <ContentWrapper>
          <Stepper form={form} />
          <FormWrapper onSubmit={handleSubmit}>
            <FormInner>
              <InformationForm form={form} setForm={setForm} />
              <InsuranceForm form={form} setForm={setForm} />
              {/* <CommonForm
                form={form}
                setForm={setForm}
                type="claimedAt"
                name="claimedAt"
                label="청구일자는 언제인가요?"
                placeholder="YYYY-MM-DD 형식으로 입력해주세요"
              /> */}
              {/* <CommonForm
                form={form}
                setForm={setForm}
                type="address"
                name="address"
                label="거주지역이 어디인가요?"
                placeholder="서울특별시 ㅇㅇ구까지 적어주세요"
              /> */}
              <CommonForm
                form={form}
                setForm={setForm}
                type="hospitalAddress"
                name="hospitalAddress"
                label="병원(지역)이 어디인가요?"
                placeholder="배상책임인 경우 사고지역을 입력해주세요"
              />
              <AccidentForm form={form} setForm={setForm} />
              <CommonForm
                form={form}
                setForm={setForm}
                type="amount"
                name="insuranceAmount"
                label="대략적인 보험 금액이 얼마일까요?"
                placeholder="보험 금액을 입력하세요"
              />
              <CommonForm
                form={form}
                setForm={setForm}
                type="contents"
                name="description"
                label="상담 내용을 간략히 적어주세요"
                placeholder="300자 내로 작성해주세요"
                textarea
              />
            </FormInner>
            <SubmitButton type="submit" disabled={disabled || mutation.isPending}>
              {mutation.isPending ? '신청 중...' : '무료 선임권 신청하기'}
            </SubmitButton>
          </FormWrapper>
        </ContentWrapper>
      </Container>
    </>
  );
};

export default AppointmentPage;

// ======================= styled-components =======================

const Container = styled.div`
  max-width: 1024px;
  margin: 0 auto;
  padding: 38px 20px;
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 25px;
  align-items: flex-start;
  position: relative;

  @media (min-width: 640px) {
    gap: 37px;
  }
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const FormInner = styled.div`
  display: grid;
  gap: 48px;

  @media (min-width: 640px) {
    gap: 64px;
  }
`;

const SubmitButton = styled.button<{ disabled: boolean }>`
  margin-top: 64px;
  width: 100%;
  padding: 21px 0;
  border-radius: 16px;
  font-size: 14px;
  text-align: center;
  font-weight: 500;
  cursor: pointer;

  @media (min-width: 640px) {
    font-size: 16px;
  }

  ${({ disabled }) =>
    disabled
      ? `
    background-color: white;
    color: #c4c4c4;
    border: 1px solid #d1d1d1;
    cursor: not-allowed;
  `
      : `
    background-color: #0B46DD;
    color: white;
    border: none;
  `}
`;

const TitleWrapper = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  margin: 50px auto 0;
  max-width: 1024px;
  padding: 0 8px;
  font-size: 24px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.48px;
  color: #000c24;
  margin-top: 80px;
  text-align: start;
`;

const TitleInner = styled.div`
  display: grid;
  gap: 11px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    align-items: flex-end;
    gap: 14px;
  }
`;
